import ReassessmentCreation from '../components/ReassessmentCreation'
import GovernedNavigation from '../../../components/GovernedNavigation';
import React, { useEffect, useRef, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { useAccess } from '../../../access/AccessContext'
import { PERMISSIONS } from '../../../access/capabilities'

import ExecutiveCard from '../../../components/executive/ExecutiveCard'
import ExecutiveSection from '../../../components/executive/ExecutiveSection'
import { formatMoneyFull } from '../../../utils/formatters'
import ProgramEvidenceReview from '../components/ProgramEvidenceReview'
import ExpectedOutcomeMeasurementList from '../components/ExpectedOutcomeMeasurementList'
import { observedResult } from '../components/ObservedOutcomeHistory'
import PortfolioReassessmentCompletionDialog from '../components/PortfolioReassessmentCompletionDialog'
import ProgramReauthorizationRequest from '../components/ProgramReauthorizationRequest'
import ReassessmentJourney, { authorityLabel, authorityDate, isLegacyObservation } from '../components/ReassessmentJourney'
import { fetchProgramAuthorizationHistory } from '../../execution_governance/investment_program/services/investmentProgramApi'
import RealizedBenefitEvidenceSection, { reviewSummary } from '../components/RealizedBenefitEvidenceSection'
import ValueRealizationAssessmentSection from '../components/ValueRealizationAssessmentSection'
import {
    capturePortfolioReassessment,
    completePortfolioReassessment,
    createObservedOutcome,
    createRealizedBenefitEvidence,
    deleteRealizedBenefitEvidence,
    deleteObservedOutcome,
    fetchEligibleCurrentEvidence,
    fetchLegacyBaselineCandidate,
    fetchEligibleValueRealizationEvidence,
    fetchBenefitsGovernance,
    fetchObservedOutcome,
    fetchObservedOutcomes,
    fetchRealizedBenefitEvidence,
    fetchPortfolioSnapshotHistory,
    reviewObservedOutcome,
    recordOutcomeJudgment,
    reviewRealizedBenefitEvidence,
    updateBenefitsAssessment,
    updateObservedOutcome,
    updateRealizedBenefitEvidence,
    updatePortfolioReassessment,
    listValueRealizationAssessments,
    createValueRealizationAssessment,
    updateValueRealizationAssessment,
    deleteValueRealizationAssessment,
    reviewValueRealizationAssessment,
    getValueRealizationReadiness,
    getValueEvidenceMetrics
} from '../services/benefitsGovernanceApi'

const input =
    'mt-1 w-full rounded-lg border border-slate-300 bg-white p-2.5 focus:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-200'
const badge =
    'inline-flex rounded-full border border-blue-200 bg-blue-50 px-2.5 py-1 text-xs font-semibold uppercase tracking-wide text-blue-700'
const unavailable = 'Not provided in the authorized Proposal Snapshot'
const reassessmentRestricted = (item) => item?.baseline_position?.detail_access === 'ACCESS_RESTRICTED' && item?.current_position?.detail_access === 'ACCESS_RESTRICTED'
const placeholder = 'Available after Portfolio Reassessment'
const benefitsIndicatorValueClassName = 'text-lg font-semibold leading-tight'
const comparabilityLabel = (status) =>
    ({ COMPARABLE: 'Comparable', PARTIALLY_COMPARABLE: 'Partially comparable', NOT_COMPARABLE: 'Not comparable' }[
        status
    ] || 'Comparability unavailable')
const snapshotCurrency = (snapshot) => snapshot?.currency || snapshot?.metadata?.currency
const snapshotMetric = (value, currency) =>
    value == null || !currency ? 'Unavailable' : formatMoneyFull(value, currency, 0)
const snapshotDate = (snapshot) => snapshot?.approved_at || snapshot?.snapshot_date
const snapshotTitle = (snapshot) => snapshot?.snapshot_name || 'Portfolio Snapshot'
export const programProjectLineageChoices = (projects) => (Array.isArray(projects) ? projects : []).map((item) => ({
    value: item.program_project_reference,
    label: item.project_name || item.name || item.program_project_reference
}))
const snapshotSummary = (snapshot) => {
    const currency = snapshotCurrency(snapshot)
    const date = snapshotDate(snapshot) ? new Date(snapshotDate(snapshot)).toLocaleDateString() : 'Date unavailable'
    return `${snapshotTitle(snapshot)} · ${date} · EAL ${snapshotMetric(
        snapshot?.expected_annual_loss,
        currency
    )} · P95 ${snapshotMetric(snapshot?.p95, currency)}`
}

const dimensionLabel = (value) => value.replaceAll('_', ' ').toLowerCase()
function ValueRealizationEvidence({ evidence }) {
    if (!evidence) return null
    return (
        <section className="rounded-lg border border-blue-100 bg-blue-50 p-4">
            <h4 className="font-semibold">Value Realization Evidence Considered</h4>
            <p className="mt-1 text-sm">Reporting period {evidence.reporting_period.start} – {evidence.reporting_period.end}</p>
            <p className="text-sm">Assessment date {evidence.assessment_as_of || 'Unavailable'} · Authorization version {evidence.authorization?.version || 'Unavailable'}</p>
            <dl className="mt-2 grid gap-2 text-sm md:grid-cols-2">
                {Object.entries(evidence.dimensions || {}).map(([key, dimension]) => (
                    <div key={key}><dt className="font-medium capitalize">{dimensionLabel(key)}</dt><dd>{dimension.conclusion} · {dimension.evidence_status}</dd></div>
                ))}
            </dl>
        </section>
    )
}

const currentEvidenceContext = (snapshot) => snapshot?.metadata?.measurement_context
export const matchingProgramCurrentDrafts = (snapshots, assessment) =>
    (snapshots || [])
        .filter((snapshot) => {
            const context = currentEvidenceContext(snapshot)
            return (
                String(snapshot?.status).toLowerCase() === 'draft' &&
                context &&
                context.purpose === 'CURRENT_PORTFOLIO_POSITION' &&
                String(context.investment_program_reference) === String(assessment.program_reference) &&
                String(context.board_decision_reference) === String(assessment.board_decision_reference) &&
                String(context.investment_proposal_reference) === String(assessment.proposal_reference) &&
                String(context.investment_proposal_version) ===
                    String(assessment.proposal_version ?? assessment.snapshot_version)
            )
        })
        .sort((left, right) => {
            const dateDifference =
                new Date(right.created_at || right.snapshot_date || 0) -
                new Date(left.created_at || left.snapshot_date || 0)
            return dateDifference || String(right.id).localeCompare(String(left.id), undefined, { numeric: true })
        })

const readableValidationError = (error, fallback) => {
    const data = error?.response?.data
    if (typeof data === 'string') return data
    if (typeof data?.detail === 'string') return data.detail
    if (data && typeof data === 'object') {
        const messages = Object.entries(data).flatMap(([field, value]) => {
            const values = Array.isArray(value) ? value : [value]
            return values.filter((message) => typeof message === 'string').map((message) => `${field}: ${message}`)
        })
        if (messages.length) return messages.join(' ')
    }
    return fallback
}

export const readableValueSaveError = (failure) => {
    const status = failure?.response?.status
    const detail = failure?.response?.data?.detail
    if (status === 401 || detail === 'Authentication credentials were not provided.') {
        return 'Your authenticated session is no longer available. Sign in again, then retry Save Draft; your unsaved assessment remains in this editor.'
    }
    if (status === 403 && typeof detail === 'string' && detail.toLowerCase().includes('csrf')) {
        return 'The security token for this session is invalid or expired. Refresh your authentication, then retry Save Draft; your unsaved assessment remains in this editor.'
    }
    if (status === 403) {
        return 'You do not have permission to create or change this Value Realization Assessment. Your unsaved assessment remains in this editor.'
    }
    return readableValidationError(failure, 'The draft could not be saved. Your unsaved assessment remains in this editor.')
}

function ApprovedSnapshotEvidence({ snapshot, currentPosition }) {
    const supplemental = !snapshot && currentPosition?.selected_role_is_published === false
    const currency = snapshotCurrency(snapshot) || currentPosition?.currency
    const eal = snapshot ? snapshot.expected_annual_loss : currentPosition?.metrics?.expected_annual_loss?.value
    const p95 = snapshot ? snapshot.p95 : currentPosition?.metrics?.p95?.value
    const date = snapshotDate(snapshot)
    return (
        <div className="rounded-lg border border-blue-200 bg-white p-4" data-testid="selected-analytical-evidence">
            <p className="font-semibold text-slate-900">
                {snapshotTitle(snapshot)}
                {date ? ` · ${new Date(date).toLocaleDateString()}` : ''}
            </p>
            <p className="mt-1 text-sm text-slate-700">
                EAL {snapshotMetric(eal, currency)} · P95 {snapshotMetric(p95, currency)}
            </p>
            <p className="mt-2 text-sm text-slate-600">{supplemental
                ? 'Supplemental captured output selected for comparison. It was not separately published or approved.'
                : 'Published metric values from the approved PortfolioSnapshot.'}</p>
        </div>
    )
}

const capturedReassessment = (item) =>
    item?.current_source_id != null && item?.current_position && Object.keys(item.current_position).length > 0

const evidenceMetricValue = (entry, currency, monetary = false) => {
    if (!entry?.available) return 'Unavailable'
    if (monetary) return currency ? formatMoneyFull(entry.value, currency, 0) : 'Unavailable'
    return String(entry.value)
}

const changeValue = (entry) => {
    if (!entry) return 'Not available'
    const signed = (value) => {
        const text = String(value)
        return Number(value) > 0 && !text.startsWith('+') ? `+${text}` : text
    }
    const change = entry.change == null ? 'Not available' : signed(entry.change)
    return entry.change_percentage != null ? `${change} (${signed(entry.change_percentage)}%)` : change
}

function ReassessmentEvidence({ reassessment }) {
    const baseline = reassessment.baseline_position || {}
    const current = reassessment.current_position || {}
    const changes = reassessment.observed_change?.metrics || {}
    const comparability = reassessment.comparability || {}
    const metrics = [
        ['expected_annual_loss', 'EAL', true],
        ['p95', 'P95', true],
        ['scenarios_addressed', 'Scenarios addressed', false]
    ]
    const checks = Array.isArray(comparability.checks)
        ? comparability.checks
        : Object.entries(comparability.checks || {}).map(([name, result]) => ({ name, ...(typeof result === 'boolean' ? { outcome: result ? 'PASS' : 'LIMITED' } : result) }))
    return (
        <div className="space-y-4" data-testid="captured-evidence">
            <section className="rounded-xl border bg-white p-5">
                <h3 className="text-lg font-semibold">Captured Evidence</h3>
                <div className="mt-4 grid gap-4 lg:grid-cols-3">
                    <article>
                        <h4 className="font-semibold">Historical analytical position</h4>
                        <dl className="mt-2 space-y-1 text-sm">
                            <div>Baseline authority: {baseline.baseline_authority || 'Unavailable'}</div>
                            <div>Evidence completeness: {baseline.evidence_completeness || 'Unavailable'}</div>
                            <div>Comparison allowed: {baseline.comparison_allowed ? 'Yes' : 'No'}</div>
                            <div>Currency: {baseline.currency || 'Unavailable'}</div>
                            {metrics.map(([key, label, monetary]) => (
                                <div key={key}>
                                    {label}: {evidenceMetricValue(baseline.metrics?.[key], baseline.currency, monetary)}
                                </div>
                            ))}
                        </dl>
                        {baseline.evidence_unavailable_reason && (
                            <p className="mt-3 text-sm text-amber-800">
                                Evidence limitation: {baseline.evidence_unavailable_reason}
                            </p>
                        )}
                    </article>
                    <article>
                        <h4 className="font-semibold">Current captured analytical position</h4>
                        <dl className="mt-2 space-y-1 text-sm">
                            <div>Currency: {current.currency || 'Unavailable'}</div>
                            {metrics.map(([key, label, monetary]) => (
                                <div key={key}>
                                    {label}: {evidenceMetricValue(current.metrics?.[key], current.currency, monetary)}
                                </div>
                            ))}
                        </dl>
                    </article>
                    <article>
                        <h4 className="font-semibold">Observed Change</h4>
                        <div className="mt-2 space-y-3 text-sm">
                            {metrics.map(([key, label, monetary]) => (
                                <div key={key}>
                                    <p className="font-medium">{label}</p>
                                    <p>
                                        Baseline:{' '}
                                        {evidenceMetricValue(baseline.metrics?.[key], baseline.currency, monetary)}
                                    </p>
                                    <p>
                                        Current:{' '}
                                        {evidenceMetricValue(current.metrics?.[key], current.currency, monetary)}
                                    </p>
                                    <p>Observed change: {changeValue(changes[key])}</p>
                                </div>
                            ))}
                        </div>
                    </article>
                </div>
            </section>
            <section className="rounded-xl border bg-white p-5" aria-labelledby="comparability-heading">
                <h3 id="comparability-heading" className="font-semibold">
                    Comparability
                </h3>
                <p className="mt-2 font-semibold">
                    {String(comparability.status || 'Unavailable').replaceAll('_', ' ')}
                </p>
                <p className="mt-1 text-sm">
                    Captured-position comparison eligibility: {comparability.governance_valid_delta ? 'Yes' : 'No'}
                </p>
                <p>Historical authorization: {baseline.selection?.authorization_reference || 'Unavailable'}</p>
                <p>Historical analytical position: {baseline.selected_analytical_role || 'Unavailable'}</p>
                <p>Evidence relationship: {baseline.selection?.evidence_relationship || 'Unavailable or restricted'}</p>
                <p>Current analytical position: {current.selected_analytical_role || 'Unavailable'}</p>
                <p>Published snapshot metric role: {current.published_metric_role || 'Unavailable'}</p>
                {current.selected_role_is_published === false && <p>Supplemental captured output selected for comparison. It was not separately published or approved.</p>}
                {comparability.limitation && <p>{comparability.limitation}</p>}
                {comparability.status === 'PARTIALLY_COMPARABLE' && <p>Differences are available with the limitations shown. This does not establish materiality, causation or realized benefit.</p>}
                {['expected_annual_loss', 'p95'].map(key => <p key={key}>{key === 'p95' ? 'P95' : 'EAL'} comparability: {comparability.metrics?.[key]?.status || 'Unavailable'} · {comparability.metrics?.[key]?.difference_allowed ? 'Captured-value difference' : 'Difference unavailable'} · {(comparability.metrics?.[key]?.reason_codes || []).join(', ')}</p>)}
                {checks.length > 0 && (
                    <ul className="mt-3 space-y-1 text-sm">
                        {checks.map((check, index) => (
                            <li key={check.name || check.check || index}>
                                <strong>
                                    {check.classification || check.status || check.outcome || 'Unavailable'}
                                </strong>{' '}
                                {String(check.label || check.name || check.check || '').replaceAll('_', ' ')}
                            </li>
                        ))}
                    </ul>
                )}
            </section>
        </div>
    )
}

function HeaderMetric({ label, children, status }) {
    return (
        <div className="rounded-xl border bg-white p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</p>
            <div className="mt-2 font-semibold text-slate-900">
                {status ? <span className={badge}>{children}</span> : children}
            </div>
        </div>
    )
}

export default function BenefitsGovernanceWorkspace() {
    const { programReference } = useParams()
    const access = useAccess()
    const canPrepareReassessment = access.hasPermission(PERMISSIONS.benefits.reassessmentChange)
    const canRecommendReassessment = access.hasPermission(PERMISSIONS.benefits.reassessmentRecommend)
    const [searchParams, setSearchParams] = useSearchParams()
    const requestedReassessmentReference = searchParams.get('reassessment_reference')
    const [assessment, setAssessment] = useState(null)
    const [error, setError] = useState('')
    const [saving, setSaving] = useState(false)
    const [draft, setDraft] = useState(null)
    const [continuingReference, setContinuingReference] = useState(null)
    const continuationHeadingRef = useRef(null)
    const continuationFocusPending = useRef(false)
    useEffect(() => {
        if (!continuationFocusPending.current || !draft ||
            draft.reassessment_reference !== continuingReference || !continuationHeadingRef.current) return
        continuationFocusPending.current = false
        continuationHeadingRef.current.focus({ preventScroll: true })
        continuationHeadingRef.current.scrollIntoView?.({ block: 'start' })
    }, [draft, continuingReference])
    const [materialitySelected, setMaterialitySelected] = useState(false)
    useEffect(() => { setMaterialitySelected(false) }, [draft?.reassessment_reference, draft?.completion_context?.token])
    const [sourceId, setSourceId] = useState('')
    const canViewProgram = access.hasPermission(PERMISSIONS.program.view)
    const [authorityHistory, setAuthorityHistory] = useState({ state: 'loading', rows: [] })
    const [historyRefresh, setHistoryRefresh] = useState(0)
    useEffect(() => {
        let active = true
        setBaselineAV('')
        if (!canViewProgram) { setAuthorityHistory({ state: 'restricted', rows: [] }); return }
        setAuthorityHistory({ state: 'loading', rows: [] })
        fetchProgramAuthorizationHistory({ programReference })
            .then(rows => { if (active) setAuthorityHistory({ state: Array.isArray(rows) ? 'ready' : 'error', rows: Array.isArray(rows) ? rows : [] }) })
            .catch(failure => { if (active) setAuthorityHistory({ state: failure.response?.status === 403 ? 'restricted' : 'error', rows: [] }) })
        return () => { active = false }
    }, [programReference, canViewProgram, historyRefresh])
    const refreshRequestLinks = async () => {
        try {
            const result = await fetchBenefitsGovernance({ programReference })
            setAssessment(current => ({ ...current, reauthorization_links: result.reauthorization_links }))
        } catch (_) { setAssessment(current => ({ ...current, reauthorization_links: null })) }
    }
    const [baselineAV, setBaselineAV] = useState('')
    const [baselineRole, setBaselineRole] = useState('')
    const [baselineRelationship, setBaselineRelationship] = useState('')
    const [legacyMode, setLegacyMode] = useState(false)
    const [legacyCandidate, setLegacyCandidate] = useState({ state: 'idle' })
    const [legacySources, setLegacySources] = useState([])
    const [legacySourceId, setLegacySourceId] = useState('')
    const [legacyRefresh, setLegacyRefresh] = useState(0)
    const canLegacy = ['investment_program.view_investmentprogram', 'board_decision.view_boarddecision',
        'digital_risk.view_portfoliosnapshot', 'digital_risk.view_fairscenario', 'portfolio_reassessment.view_portfolioreassessment'].every(p => access.hasPermission(p))
    const legacyDraft = isLegacyObservation(draft)
    const restrictedDraft = reassessmentRestricted(draft)
    const clearLegacy = () => { setLegacyCandidate({ state: 'restricted' }); setLegacySources([]); setLegacySourceId('') }
    useEffect(() => {
        let active = true
        setLegacySources([]); setLegacySourceId('')
        if (!legacyMode || !baselineAV || !canLegacy || draft) {
            setLegacyCandidate({ state: canLegacy ? 'idle' : 'restricted' }); return
        }
        setLegacyCandidate({ state: 'loading' })
        fetchLegacyBaselineCandidate({ programReference, authorizationReference: baselineAV })
            .then(async candidate => {
                if (!active) return
                const sources = await fetchEligibleCurrentEvidence({ programReference })
                if (!active) return
                setLegacyCandidate({ state: 'ready', reference: baselineAV, value: candidate })
                setLegacySources(sources)
            }).catch(failure => {
                if (!active) return
                setLegacyCandidate({ state: failure.response?.status === 403 ? 'restricted' : 'error',
                    message: readableValidationError(failure, 'Governed legacy evidence could not be resolved.') })
            })
        return () => { active = false }
    }, [legacyMode, baselineAV, canLegacy, programReference, legacyRefresh, draft])
    const [selectedRole, setSelectedRole] = useState('')
    const [approvedSnapshots, setApprovedSnapshots] = useState([])
    const [valueEvidenceCandidates, setValueEvidenceCandidates] = useState([])
    const [snapshotsLoading, setSnapshotsLoading] = useState(false)
    const [snapshotsError, setSnapshotsError] = useState(false)
    const [snapshotsLoadedFor, setSnapshotsLoadedFor] = useState(null)
    const [newType, setNewType] = useState('INTERIM')
    const [pendingDrafts, setPendingDrafts] = useState([])
    const [historyError, setHistoryError] = useState(false)
    const [approvalMessage, setApprovalMessage] = useState('')
    const [evidenceRefresh, setEvidenceRefresh] = useState(0)
    const [completionDialogOpen, setCompletionDialogOpen] = useState(false)
    const [completing, setCompleting] = useState(false)
    const completeButtonRef = useRef(null)
    const completionInFlightRef = useRef(false)
    const [observations, setObservations] = useState([])
    const [observationsLoading, setObservationsLoading] = useState(true)
    const [observationsUnavailable, setObservationsUnavailable] = useState(false)
    const [outcomeEditor, setOutcomeEditor] = useState(null)
    const [outcomeError, setOutcomeError] = useState('')
    const [outcomeBusy, setOutcomeBusy] = useState(false)
    const [benefitEvidence, setBenefitEvidence] = useState([])
    const [benefitEvidenceLoading, setBenefitEvidenceLoading] = useState(true)
    const [benefitEvidenceUnavailable, setBenefitEvidenceUnavailable] = useState(false)
    const [benefitEvidenceEditor, setBenefitEvidenceEditor] = useState(null)
    const [benefitEvidenceError, setBenefitEvidenceError] = useState('')
    const [benefitEvidenceBusy, setBenefitEvidenceBusy] = useState(false)
    const [valueAssessments, setValueAssessments] = useState([])
    const [valueAssessmentsLoading, setValueAssessmentsLoading] = useState(true)
    const [valueSupportingLoading, setValueSupportingLoading] = useState(true)
    const [valueReadiness, setValueReadiness] = useState(null)
    const [valueMetrics, setValueMetrics] = useState(null)
    const [valueError, setValueError] = useState('')
    const [valueSupportingError, setValueSupportingError] = useState('')
    const [valueBusy, setValueBusy] = useState(false)
    const combinePermissions = (domain = {}, mapping = {}) => Object.fromEntries(Object.entries(mapping).map(([key, permission]) => [key, domain?.[key] === true && access.hasPermission(permission)]))
    const observedPermissions = combinePermissions(assessment?.observed_outcome_permissions, { view: PERMISSIONS.benefits.observedView, create: PERMISSIONS.benefits.observedAdd, change: PERMISSIONS.benefits.observedChange, delete: PERMISSIONS.benefits.observedDelete, review: PERMISSIONS.benefits.observedReview })
    const evidencePermissions = combinePermissions(assessment?.realized_benefit_evidence_permissions, { view: PERMISSIONS.benefits.evidenceView, create: PERMISSIONS.benefits.evidenceAdd, change: PERMISSIONS.benefits.evidenceChange, delete: PERMISSIONS.benefits.evidenceDelete, review: PERMISSIONS.benefits.evidenceReview })
    const valuePermissions = combinePermissions(assessment?.value_realization_assessment_permissions, { view: PERMISSIONS.benefits.realizationView, create: PERMISSIONS.benefits.realizationAdd, change: PERMISSIONS.benefits.realizationChange, delete: PERMISSIONS.benefits.realizationDelete, review: PERMISSIONS.benefits.realizationReview, correct: PERMISSIONS.benefits.realizationCorrect })
    const capabilityContract = assessment ? { ...assessment, value_realization_assessment_permissions: valuePermissions } : assessment
    useEffect(() => {
        let active = true
        fetchBenefitsGovernance({ programReference })
            .then((data) => active && setAssessment(data))
            .catch(() => active && setError('Benefits Governance could not be loaded.'))
        return () => {
            active = false
        }
    }, [programReference])
    const reloadValueAssessments = async () => {
        try {
            const rows = await listValueRealizationAssessments({ programReference })
            setValueAssessments(Array.isArray(rows) ? rows : [])
            setValueError('')
        } catch (failure) {
            setValueError(readableValidationError(failure, 'Value Realization Assessment history could not be loaded.'))
        } finally { setValueAssessmentsLoading(false) }
    }
    useEffect(() => {
        if (!assessment) return
        const permissions = assessment.value_realization_assessment_permissions
        if (typeof permissions?.view !== 'boolean' || !permissions.view) {
            setValueAssessmentsLoading(false); setValueSupportingLoading(false); return
        }
        reloadValueAssessments()
        Promise.allSettled([getValueRealizationReadiness({ programReference }), getValueEvidenceMetrics({ programReference })])
            .then(([readinessResult, metricsResult]) => {
                if (readinessResult.status === 'fulfilled') setValueReadiness(readinessResult.value)
                if (metricsResult.status === 'fulfilled') setValueMetrics(metricsResult.value)
                if (readinessResult.status === 'rejected' || metricsResult.status === 'rejected') setValueSupportingError('Current readiness or supporting mechanical metrics are unavailable. Assessment history remains authoritative.')
            }).finally(() => setValueSupportingLoading(false))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [assessment, programReference])
    const previewValueEvidence = async (dimension, evidenceSources, periodStart, periodEnd) => {
        setValueSupportingLoading(true); setValueSupportingError('')
        try {
            const preview = await getValueEvidenceMetrics({
                programReference, dimension, evidenceSources,
                periodStart, periodEnd
            })
            setValueMetrics((current) => ({ ...(current || {}), dimensions: {
                ...(current?.dimensions || {}), [dimension]: preview
            } }))
        } catch (failure) {
            setValueSupportingError(readableValidationError(failure, 'Supporting mechanical metrics could not be recalculated.'))
        } finally { setValueSupportingLoading(false) }
    }
    const valuePayload = (draftValue) => ({
        authorization_reference: draftValue.authorization_reference,
        period_start: draftValue.period_start, period_end: draftValue.period_end,
        ...(draftValue.supersedes_assessment_reference ? { supersedes_assessment_reference: draftValue.supersedes_assessment_reference } : {}),
        correction_reason: draftValue.correction_reason || '', assessment_commentary: draftValue.assessment_commentary || '',
        dimensions: draftValue.dimensions
    })
    const saveValueAssessment = async (draftValue) => {
        setValueBusy(true); setValueError('')
        try {
            if (draftValue.assessment_reference) await updateValueRealizationAssessment({ reference: draftValue.assessment_reference, payload: valuePayload(draftValue) })
            else await createValueRealizationAssessment({ programReference, payload: valuePayload(draftValue) })
            await reloadValueAssessments()
        } catch (failure) { setValueError(readableValueSaveError(failure)); throw failure }
        finally { setValueBusy(false) }
    }
    const removeValueAssessment = async (record) => {
        if (!window.confirm('Delete this draft Value Realization Assessment?')) return
        setValueBusy(true)
        try { await deleteValueRealizationAssessment({ reference: record.assessment_reference }); await reloadValueAssessments() }
        catch (failure) { setValueError(readableValidationError(failure, 'The draft could not be deleted.')) }
        finally { setValueBusy(false) }
    }
    const finalizeValueAssessment = async (record) => {
        setValueBusy(true); setValueError('')
        try { await reviewValueRealizationAssessment({ reference: record.assessment_reference }); await reloadValueAssessments() }
        catch (failure) { setValueError(readableValidationError(failure, 'Review could not be completed.')); throw failure }
        finally { setValueBusy(false) }
    }
    const reloadBenefitEvidence = async () => {
        try {
            const rows = await fetchRealizedBenefitEvidence({ programReference })
            setBenefitEvidence(Array.isArray(rows) ? rows : [])
            setBenefitEvidenceUnavailable(false)
        } catch (failure) {
            setBenefitEvidenceUnavailable(true)
            if (failure?.response?.status === 403) setBenefitEvidenceError('You do not have permission to view realized-benefit evidence.')
        } finally {
            setBenefitEvidenceLoading(false)
        }
    }
    useEffect(() => {
        setBenefitEvidenceLoading(true)
        reloadBenefitEvidence()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [programReference])
    const reloadObservations = async () => {
        try {
            const rows = await fetchObservedOutcomes({ programReference })
            setObservations(Array.isArray(rows) ? rows : [])
            setObservationsUnavailable(false)
        } catch {
            setObservationsUnavailable(true)
        } finally {
            setObservationsLoading(false)
        }
    }
    useEffect(() => {
        setObservationsLoading(true)
        reloadObservations()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [programReference])
    useEffect(() => {
        if (!draft || !assessment || restrictedDraft || (legacyDraft && !canLegacy)) return undefined
        const reassessmentReference = draft.reassessment_reference
        let active = true
        setSnapshotsLoading(true)
        setSnapshotsError(false)
        setHistoryError(false)
        Promise.allSettled([fetchEligibleCurrentEvidence({ programReference }), fetchPortfolioSnapshotHistory(), fetchEligibleValueRealizationEvidence({ programReference })])
            .then(([eligibleResult, historyResult, valueResult]) => {
                if (!active) return
                if (historyResult.status === 'fulfilled') {
                    setPendingDrafts(matchingProgramCurrentDrafts(historyResult.value, assessment))
                } else {
                    setPendingDrafts([])
                    setHistoryError(true)
                }
                if (eligibleResult.status === 'rejected') {
                    setApprovedSnapshots([])
                    setSnapshotsError(true)
                    return
                }
                const snapshots = eligibleResult.value
                setApprovedSnapshots(snapshots)
                setValueEvidenceCandidates(
                    valueResult.status === 'fulfilled' && Array.isArray(valueResult.value) ? valueResult.value : []
                )
                setSourceId((selected) => {
                    if (selected && snapshots.some((snapshot) => String(snapshot.id) === String(selected)))
                        return selected
                    if (
                        draft.current_source_id &&
                        snapshots.some((snapshot) => String(snapshot.id) === String(draft.current_source_id))
                    )
                        return String(draft.current_source_id)
                    return snapshots.length === 1 ? String(snapshots[0].id) : ''
                })
            })
            .finally(() => {
                if (active) {
                    setSnapshotsLoading(false)
                    setSnapshotsLoadedFor(reassessmentReference)
                }
            })
        return () => {
            active = false
        }
    }, [draft, programReference, assessment, evidenceRefresh, restrictedDraft, legacyDraft, canLegacy])
    const change = (field) => (event) => setAssessment((current) => ({ ...current, [field]: event.target.value }))
    const save = async (event) => {
        event.preventDefault()
        setSaving(true)
        setError('')
        try {
            setAssessment(
                await updateBenefitsAssessment({
                    programReference,
                    payload: {
                        delivery_status: assessment.delivery_status,
                        benefits_confidence: assessment.benefits_confidence,
                        management_commentary: assessment.management_commentary,
                        assessment_date: assessment.assessment_date || null
                    }
                })
            )
        } catch {
            setError('The Management assessment could not be saved.')
        } finally {
            setSaving(false)
        }
    }
    if (!assessment && !error) return <main className="p-8 text-slate-600">Loading Benefits Governance…</main>
    if (!assessment)
        return (
            <main className="p-8 text-red-800" role="alert">
                {error}
            </main>
        )
    const expected = assessment.expected_outcomes || {}
    const history =
        assessment.portfolio_reassessment_history ||
        (assessment.latest_portfolio_reassessment ? [assessment.latest_portfolio_reassessment] : [])
    const contextualReassessment = requestedReassessmentReference
        ? history.find((item) => String(item.reassessment_reference) === requestedReassessmentReference)
        : null
    const invalidReassessmentContext = Boolean(requestedReassessmentReference && !contextualReassessment)
    const reassessment = invalidReassessmentContext
        ? null
        : contextualReassessment?.status === 'COMPLETED'
            ? contextualReassessment
            : history.find((item) => item.status === 'COMPLETED')
    const existingDraft = history.find((item) => item.status === 'DRAFT')
    const snapshotEvidenceLoading =
        Boolean(draft) && (snapshotsLoading || snapshotsLoadedFor !== draft.reassessment_reference)
    const isCapturedDraft = capturedReassessment(draft)
    const finalCompleted = history.some((item) => item.status === 'COMPLETED' && item.reassessment_type === 'FINAL')
    const capture = async () => {
        if (!sourceId || snapshotEvidenceLoading || snapshotsError) return
        setError('')
        try {
            const payload = {
                assessment_date: draft.assessment_date,
                management_commentary: draft.management_commentary,
                reassessment_trigger: draft.reassessment_trigger,
                material_change_assessment: draft.material_change_assessment,
                value_realization_assessment_reference: draft.value_realization_evidence?.assessment_reference || null
            }
            if (draft.management_recommendation) {
                payload.management_recommendation = draft.management_recommendation
            }
            if (draft.continued_investment_conclusion) {
                payload.continued_investment_conclusion = draft.continued_investment_conclusion
            }
            let item = await updatePortfolioReassessment({
                reference: draft.reassessment_reference,
                payload
            })
            item = await capturePortfolioReassessment({ reference: item.reassessment_reference, sourceId, ...(!legacyDraft && selectedRole ? { selectedAnalyticalRole: selectedRole } : {}) })
            setDraft(item)
        } catch (captureFailure) {
            if (legacyDraft && captureFailure.response?.status === 403) { clearLegacy(); setDraft(current => ({ reassessment_reference: current.reassessment_reference, status: current.status, baseline_position: { detail_access: 'ACCESS_RESTRICTED' }, current_position: { detail_access: 'ACCESS_RESTRICTED' } })) }
            setError(
                readableValidationError(
                    captureFailure,
                    'The selected approved Portfolio Snapshot could not be captured.'
                )
            )
        }
    }
    const complete = async (acknowledgment) => {
        if (completionInFlightRef.current) return
        completionInFlightRef.current = true
        setCompleting(true)
        setError('')
        try {
            const item = await completePortfolioReassessment({
                reference: draft.reassessment_reference,
                payload: {
                    assessment_date: draft.assessment_date,
                    management_commentary: draft.management_commentary,
                    reassessment_trigger: draft.reassessment_trigger,
                    material_change_assessment: draft.material_change_assessment,
                    management_recommendation: draft.management_recommendation,
                    ...(draft.reassessment_type !== 'FINAL' ? { continued_investment_conclusion: draft.continued_investment_conclusion } : {}),
                    evidence_acknowledgment: acknowledgment,
                    value_realization_assessment_reference: draft.value_realization_evidence?.assessment_reference || null
                },
            })
            setCompletionDialogOpen(false)
            setDraft(null)
            setAssessment((current) => ({
                ...current,
                latest_portfolio_reassessment: item,
                portfolio_reassessment_history: [item, ...(current.portfolio_reassessment_history || [])
                    .filter(entry => entry.reassessment_reference !== item.reassessment_reference)]
            }))
        } catch (failure) {
            if (legacyDraft && failure.response?.status === 403) { clearLegacy(); setDraft(current => ({ reassessment_reference: current.reassessment_reference, status: current.status, baseline_position: { detail_access: 'ACCESS_RESTRICTED' }, current_position: { detail_access: 'ACCESS_RESTRICTED' } })) }
            setCompletionDialogOpen(false)
            if (failure?.response?.data?.evidence_acknowledgment) {
                setMaterialitySelected(false)
                setDraft(current => ({ ...current, completion_context: { supported: false } }))
            }
            setError(failure?.response?.status === 403
                ? 'You do not have Management permission to complete and freeze this Portfolio Reassessment.'
                : readableValidationError(failure, 'Portfolio Reassessment could not be completed.'))
        } finally {
            completionInFlightRef.current = false
            setCompleting(false)
        }
    }
    const cancelCompletion = () => {
        setCompletionDialogOpen(false)
        completeButtonRef.current?.focus()
    }
    const outcomeFailureMessage = (failure, action) => {
        const status = failure?.response?.status
        if (status === 403) return action === 'review' ? 'You do not have permission to review this observation.' : 'You do not have permission to record observed outcomes.'
        if (status === 404) return 'This observed outcome is no longer available.'
        if (status === 409) return 'This observation’s governance state changed. Evidence has been refreshed.'
        return readableValidationError(failure, 'Observed outcome evidence could not be saved.')
    }
    const saveObservedOutcome = async (payload) => {
        setOutcomeBusy(true)
        setOutcomeError('')
        try {
            if (outcomeEditor.observation) await updateObservedOutcome({ reference: outcomeEditor.observation.observed_outcome_reference, payload })
            else await createObservedOutcome({ programReference, payload })
            setOutcomeEditor(null)
            await reloadObservations()
        } catch (failure) {
            setOutcomeError(outcomeFailureMessage(failure, 'save'))
            if (failure?.response?.status === 409) await reloadObservations()
        } finally { setOutcomeBusy(false) }
    }
    const removeObservedOutcome = async (item) => {
        if (!window.confirm(`Delete draft ${item.observed_outcome_reference}?`)) return
        try { await deleteObservedOutcome({ reference: item.observed_outcome_reference }); await reloadObservations() }
        catch (failure) { setOutcomeError(outcomeFailureMessage(failure, 'delete')); if (failure?.response?.status === 409) await reloadObservations() }
    }
    const reviewObservation = async (item) => {
        const summary = `Review this evidence?\n\nExpected Outcome: ${item.expected_outcome_statement}\nObserved Result: ${observedResult(item)}\nObservation Period: ${item.observation_period_start} – ${item.observation_period_end}\nMeasurement Source: ${item.measurement_source || 'Not provided'}\nSource Reference: ${item.source_reference || 'Not provided'}\nEvidence Quality: ${item.evidence_quality || 'Not provided'}`
        if (!window.confirm(summary)) return
        setOutcomeError('')
        try {
            await reviewObservedOutcome({ reference: item.observed_outcome_reference })
            await reloadObservations()
        } catch (failure) {
            setOutcomeError(outcomeFailureMessage(failure, 'review'))
            if (failure?.response?.status === 409) {
                try { await fetchObservedOutcome({ reference: item.observed_outcome_reference }) } catch { /* list refresh remains authoritative */ }
                await reloadObservations()
            }
        }
    }
    const recordJudgment = async (item, payload) => {
        setOutcomeBusy(true)
        setOutcomeError('')
        try {
            await recordOutcomeJudgment({ reference: item.evaluation.evaluation_reference, payload })
            await reloadObservations()
            setAssessment(await fetchBenefitsGovernance({ programReference }))
        } catch (failure) {
            setOutcomeError(readableValidationError(failure, 'Governed outcome judgment could not be recorded.'))
        } finally { setOutcomeBusy(false) }
    }
    const saveBenefitEvidence = async (payload) => {
        setBenefitEvidenceBusy(true)
        setBenefitEvidenceError('')
        try {
            if (benefitEvidenceEditor.record) {
                const mutablePayload = { ...payload }
                ;['expected_outcome_reference', 'observed_outcome_reference', 'outcome_evaluation_reference', 'program_project_reference'].forEach((field) => delete mutablePayload[field])
                await updateRealizedBenefitEvidence({ reference: benefitEvidenceEditor.record.benefit_evidence_reference, payload: mutablePayload })
            }
            else await createRealizedBenefitEvidence({ programReference, payload })
            setBenefitEvidenceEditor(null)
            await reloadBenefitEvidence()
        } catch (failure) {
            setBenefitEvidenceError(readableValidationError(failure, 'Benefit evidence could not be saved.'))
        } finally { setBenefitEvidenceBusy(false) }
    }
    const removeBenefitEvidence = async (item) => {
        if (!window.confirm(`Delete draft benefit evidence ${item.benefit_evidence_reference}?`)) return
        setBenefitEvidenceError('')
        try { await deleteRealizedBenefitEvidence({ reference: item.benefit_evidence_reference }); await reloadBenefitEvidence() }
        catch (failure) { setBenefitEvidenceError(readableValidationError(failure, 'Benefit evidence could not be deleted.')); if (failure?.response?.status === 409) await reloadBenefitEvidence() }
    }
    const reviewBenefitEvidence = async (item) => {
        if (!window.confirm(reviewSummary(item, assessment.realized_benefit_evidence_choices || {}))) return
        setBenefitEvidenceError('')
        try { await reviewRealizedBenefitEvidence({ reference: item.benefit_evidence_reference }); await reloadBenefitEvidence() }
        catch (failure) { setBenefitEvidenceError(readableValidationError(failure, 'Benefit evidence could not be reviewed.')); if (failure?.response?.status === 409) await reloadBenefitEvidence() }
    }
    const expectedItems = [
        ['Expected Resilience Improvement', 'expected_resilience_improvement'],
        ['Investment Rationale', 'investment_rationale'],
        ['Executive Summary', 'executive_summary'],
        ['Strategic Objective', 'strategic_objective'],
        ['Key Assumptions', 'key_assumptions'],
        ['Dependencies', 'dependencies']
    ]
    const timeline = [
        'Investment Proposal',
        'Board Decision',
        'Investment Program',
        'Program Execution',
        'Benefits Governance',
        'Portfolio Reassessment',
        'Executive Reporting'
    ]
    return (
        <main className="min-h-screen min-w-0 bg-slate-50 p-4 sm:p-6" data-testid="benefits-governance-page-content">
            <div className="min-w-0 space-y-5" data-testid="benefits-governance-width-owner">
                <header className="rounded-2xl border bg-white p-5 sm:p-6">
                    <p className="text-xs font-semibold uppercase tracking-widest text-blue-700">
                        Benefits Realization Governance
                    </p>
                    <h1 className="mt-1 text-3xl font-bold text-slate-950">Benefits Governance</h1>
                    <p className="mt-1 text-slate-600">
                        Management assessment of whether the Board-authorized investment remains on track to deliver the
                        expected resilience outcomes.
                    </p>
                    <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
                        <HeaderMetric label="Program Reference">
                            {assessment.program_name || 'Investment Program'}
                        </HeaderMetric>
                        <HeaderMetric label="Current Governance Status" status>
                            {assessment.governance_status}
                        </HeaderMetric>
                        <HeaderMetric label="Program Manager">
                            {assessment.program_manager || 'Unassigned'}
                        </HeaderMetric>
                        <HeaderMetric label="Delivery Status" status={assessment.is_effective}>
                            {assessment.is_effective ? assessment.delivery_status.replaceAll('_', ' ') : 'Not assessed'}
                        </HeaderMetric>
                        <HeaderMetric label="Benefits Confidence" status={assessment.is_effective}>
                            {assessment.is_effective ? assessment.benefits_confidence : 'Unassessed'}
                        </HeaderMetric>
                    </div>
                </header>
                {approvalMessage && (
                    <div role="status" className="rounded-xl border border-green-200 bg-green-50 p-4 text-green-800">
                        {approvalMessage}
                    </div>
                )}
                {error && (
                    <div role="alert" className="rounded-xl border border-red-200 bg-red-50 p-4 text-red-800">
                        {error}
                    </div>
                )}
                <ExecutiveSection
                    title="Expected Outcomes"
                    subtitle="What resilience outcomes did the Board authorize? Read-only values from the immutable Investment Proposal Snapshot."
                >
                    {(expected.normalized || []).length > 0 ? <ol className="mb-4 space-y-3">{expected.normalized.map((outcome) => <li className="rounded-xl border border-blue-200 bg-blue-50 p-5" key={outcome.expected_outcome_reference}><p className="leading-relaxed text-slate-800">{outcome.outcome_statement}</p><p className="mt-2 font-mono text-xs text-slate-500">Reference: {outcome.expected_outcome_reference}</p></li>)}</ol> : <p className="mb-4 rounded-lg bg-amber-50 p-3 text-sm text-amber-900">Legacy evidence — individual expected outcomes are not addressable.</p>}
                    <div className="grid gap-4 md:grid-cols-2">
                        {expectedItems.map(([label, key], index) => (
                            <article
                                key={key}
                                className={`rounded-xl border p-5 ${
                                    index === 0 ? 'border-blue-200 bg-blue-50' : 'bg-white'
                                }`}
                            >
                                <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                                    {label}
                                </h3>
                                <p className="mt-2 whitespace-pre-wrap leading-relaxed text-slate-800">
                                    {expected[key] || unavailable}
                                </p>
                            </article>
                        ))}
                    </div>
                    <article className="mt-4 rounded-xl border bg-white p-5">
                        <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                            Expected Risk Reduction
                        </h3>
                        <p className="mt-2 text-slate-800">
                            {expected.expected_risk_reduction || 'Not quantified in the submitted Proposal'}
                        </p>
                    </article>
                </ExecutiveSection>
                <ExecutiveSection
                    title="Outcome Measurement Evidence"
                    subtitle="Did observed evidence satisfy the authorized success criterion? Outcome Measurement remains distinct from Benefit Evidence."
                >
                    {outcomeError && !outcomeEditor && <p role="alert" className="mb-3 rounded bg-red-50 p-3 text-red-800">{outcomeError}</p>}
                    <ExpectedOutcomeMeasurementList
                        outcomes={(expected.normalized || []).length ? expected.normalized : [{ outcome_statement: expected.expected_resilience_improvement || expected.strategic_objective || 'Historical expected outcome', addressability: 'LEGACY_UNADDRESSABLE' }]}
                        observations={observations}
                        loading={observationsLoading}
                        unavailable={observationsUnavailable}
                        permissions={observedPermissions}
                        projects={assessment.program_projects || []}
                        reassessments={assessment.portfolio_reassessment_history || []}
                        editing={outcomeEditor}
                        formError={outcomeError}
                        judgmentError={outcomeError}
                        busy={outcomeBusy}
                        onStart={(outcome) => { setOutcomeError(''); setOutcomeEditor({ outcome }) }}
                        onCancel={() => { setOutcomeEditor(null); setOutcomeError('') }}
                        onSave={saveObservedOutcome}
                        onEdit={(observation) => { setOutcomeError(''); setOutcomeEditor({ outcome: expected.normalized.find((item) => item.expected_outcome_reference === observation.expected_outcome_reference), observation }) }}
                        onDelete={removeObservedOutcome}
                        onReview={reviewObservation}
                        onJudge={recordJudgment}
                    />
                </ExecutiveSection>
                <ExecutiveSection
                    title="Realized Benefit Evidence"
                    subtitle="Governed records of observed, measured, estimated, documented, or model-derived benefit evidence; not a Value Realization conclusion."
                >
                    <RealizedBenefitEvidenceSection
                        records={benefitEvidence}
                        loading={benefitEvidenceLoading}
                        unavailable={benefitEvidenceUnavailable}
                        permissions={evidencePermissions}
                        choices={assessment.realized_benefit_evidence_choices || {}}
                        editor={benefitEvidenceEditor}
                        error={benefitEvidenceError}
                        busy={benefitEvidenceBusy}
                        lineage={{
                            expectedOutcomes: (expected.normalized || []).map((item) => ({ value: item.expected_outcome_reference, label: item.outcome_statement })),
                            observedOutcomes: observations.map((item) => ({ value: item.observed_outcome_reference, label: item.observed_outcome_statement || item.observed_outcome_reference })),
                            evaluations: observations.filter((item) => item.outcome_evaluation_reference).map((item) => ({ value: item.outcome_evaluation_reference, label: item.outcome_evaluation_reference })),
                            projects: programProjectLineageChoices(assessment.program_projects)
                        }}
                        onCreate={() => { setBenefitEvidenceError(''); setBenefitEvidenceEditor({}) }}
                        onEdit={(record) => { setBenefitEvidenceError(''); setBenefitEvidenceEditor({ record }) }}
                        onCancel={() => { setBenefitEvidenceEditor(null); setBenefitEvidenceError('') }}
                        onSave={saveBenefitEvidence}
                        onDelete={removeBenefitEvidence}
                        onReview={reviewBenefitEvidence}
                    />
                </ExecutiveSection>
                <ExecutiveSection
                    title="Value Realization Assessment"
                    subtitle="What does the governed evidence support for each value dimension?"
                >
                    <ValueRealizationAssessmentSection
                        programName={assessment.program_name || assessment.program_title || assessment.program_reference}
                        contract={capabilityContract}
                        records={valueAssessments}
                        loading={valueAssessmentsLoading}
                        supportingLoading={valueSupportingLoading}
                        readiness={valueReadiness}
                        metrics={valueMetrics}
                        error={valueError}
                        supportingError={valueSupportingError}
                        busy={valueBusy}
                        onSave={saveValueAssessment}
                        onDelete={removeValueAssessment}
                        onReview={finalizeValueAssessment}
                        onPreview={previewValueEvidence}
                    />
                </ExecutiveSection>
                <ExecutiveSection
                    title="Benefits Realization Assessment"
                    subtitle="Are we on track to realize the benefits authorized by the Board?"
                >
                    <form onSubmit={save} className="space-y-4">
                        <div className="grid gap-4 md:grid-cols-2">
                            <label className="font-medium">
                                Program Delivery Status
                                <span className="block text-sm font-normal text-slate-500">
                                    Management’s view of whether the authorized Program is progressing as expected.
                                </span>
                                <select
                                    aria-label="Program Delivery Status"
                                    className={input}
                                    value={assessment.delivery_status}
                                    onChange={change('delivery_status')}
                                >
                                    {['ON_TRACK', 'AT_RISK', 'BEHIND_PLAN', 'COMPLETED'].map((value) => (
                                        <option key={value} value={value}>
                                            {value.replaceAll('_', ' ')}
                                        </option>
                                    ))}
                                </select>
                            </label>
                            <label className="font-medium">
                                Benefits Confidence
                                <span className="block text-sm font-normal text-slate-500">
                                    Management’s confidence that the expected resilience outcomes will ultimately be
                                    realized.
                                </span>
                                <select
                                    aria-label="Benefits Confidence"
                                    className={input}
                                    value={assessment.benefits_confidence}
                                    onChange={change('benefits_confidence')}
                                >
                                    {['UNASSESSED', 'HIGH', 'MEDIUM', 'LOW'].map((value) => (
                                        <option key={value}>{value}</option>
                                    ))}
                                </select>
                            </label>
                        </div>
                        <label className="block font-medium">
                            Management Executive Commentary
                            <textarea
                                aria-label="Management Executive Commentary"
                                className={`${input} min-h-28`}
                                value={assessment.management_commentary || ''}
                                onChange={change('management_commentary')}
                            />
                        </label>
                        <label className="block max-w-md font-medium">
                            Assessment Date
                            <input
                                aria-label="Assessment Date"
                                type="date"
                                className={input}
                                value={assessment.assessment_date || ''}
                                onChange={change('assessment_date')}
                            />
                        </label>
                        <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-slate-500">
                            <span>
                                {assessment.is_effective
                                    ? `Last Updated: ${new Date(
                                          assessment.updated_at
                                      ).toLocaleString()} · Updated By: ${assessment.updated_by || 'Unavailable'}`
                                    : 'Assessment not yet performed'}
                            </span>
                            {access.hasPermission(PERMISSIONS.benefits.assess) && <button
                                disabled={saving}
                                className="rounded-lg bg-blue-700 px-4 py-2 font-semibold text-white disabled:opacity-60"
                            >
                                {saving ? 'Saving…' : 'Save Assessment'}
                            </button>}
                        </div>
                    </form>
                </ExecutiveSection>
                <ExecutiveSection
                    title="Benefits Indicators"
                    subtitle="What evidence is currently available about expected and observed outcomes?"
                >
                    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
                        <ExecutiveCard
                            title="Expected Outcomes"
                            value="Available from Proposal narrative"
                            subtitle="Not a calculated score."
                            valueClassName={benefitsIndicatorValueClassName}
                        />
                        <ExecutiveCard
                            title="Observed Outcomes"
                            value={reassessment ? 'Available from completed reassessment' : placeholder}
                            subtitle="Descriptive measurement only."
                            valueClassName={benefitsIndicatorValueClassName}
                        />
                        <ExecutiveCard
                            title="Benefits Achievement"
                            value="Interpretation pending"
                            subtitle="No achievement percentage is calculated."
                            valueClassName={benefitsIndicatorValueClassName}
                        />
                        <ExecutiveCard
                            title="Portfolio Reassessment"
                            value={reassessment ? 'Completed' : 'Not yet performed'}
                            subtitle="Captured evidence; no causal attribution."
                            valueClassName={benefitsIndicatorValueClassName}
                        />
                        <ExecutiveCard
                            title="Next Review"
                            value="Not scheduled"
                            subtitle="Assessment Date is not a future review date."
                            valueClassName={benefitsIndicatorValueClassName}
                        />
                    </div>
                </ExecutiveSection>
                <ProgramEvidenceReview programReference={programReference} onApproved={() => {
                    setApprovalMessage('Current analytical evidence APPROVED.')
                    setEvidenceRefresh(value => value + 1)
                    setLegacyRefresh(value => value + 1)
                }} />
                <ExecutiveSection
                    title="Portfolio Reassessment"
                    subtitle="Baseline authorization evidence compared with an explicitly selected approved analytical snapshot."
                >
                    {draft && continuingReference === draft.reassessment_reference && (
                        <div className="mb-4 rounded-xl border border-blue-200 bg-blue-50 p-4">
                            <h3 ref={continuationHeadingRef} tabIndex={-1} className="font-semibold">
                                Continuing Reassessment #{draft.sequence}
                            </h3>
                            <p>Status: {draft.status}</p>
                            <p className="text-xs text-slate-600">UUID: {draft.reassessment_reference}</p>
                            <p>Pending step: {capturedReassessment(draft) ? 'Management assessment and completion review' : 'Capture current risk position'}</p>
                        </div>
                    )}
                    {(draft || reassessment) && access.hasPermission(PERMISSIONS.benefits.reassessmentView) && <ReassessmentJourney
                        key={(draft || reassessment).reassessment_reference}
                        reassessment={isLegacyObservation(draft || reassessment) && !canLegacy ? { status: (draft || reassessment).status, baseline_position: { detail_access: 'ACCESS_RESTRICTED' }, current_position: { detail_access: 'ACCESS_RESTRICTED' } } : draft || reassessment}
                        authorityHistory={canViewProgram ? authorityHistory : { state: 'restricted', rows: [] }}
                        cohortRestricted={!access.hasPermission(PERMISSIONS.fair.view) || (draft || reassessment).baseline_position?.detail_access === 'ACCESS_RESTRICTED' || (draft || reassessment).current_position?.detail_access === 'ACCESS_RESTRICTED'}
                        onRetryHistory={() => setHistoryRefresh(n => n + 1)}
                        nextAction={<>
                            {(draft || reassessment).status === 'COMPLETED' &&
                                (draft || reassessment).management_recommendation === 'FURTHER_ANALYSIS_REQUIRED' && (
                                    <GovernedNavigation
                                        className="mb-3 inline-flex rounded-lg bg-blue-700 px-4 py-2 font-semibold text-white"
                                        to={`/strategic-analysis?program=${encodeURIComponent(programReference)}&returnTo=${encodeURIComponent(`/benefits-governance/${programReference}${searchParams.toString() ? `?${searchParams.toString()}` : ''}`)}`}
                                    >
                                        Continue Further Analysis in Workspace
                                    </GovernedNavigation>
                                )}
                            <ProgramReauthorizationRequest
                            key={(draft || reassessment).reassessment_reference}
                            programReference={assessment.program_reference}
                            reassessment={draft || reassessment}
                            links={assessment.reauthorization_links}
                            canManage={access.hasPermission('program_reauthorization.manage_program_reauthorization')}
                            canViewBoard={access.hasPermission(PERMISSIONS.board.view)}
                            authorizationHistory={canViewProgram && authorityHistory.state === 'ready' ? authorityHistory.rows : []}
                            onRefreshLinks={refreshRequestLinks}
                            />
                        </>}
                    />}
                    {invalidReassessmentContext && (
                        <p role="alert" className="mb-4 rounded-xl border border-amber-200 bg-amber-50 p-4 text-amber-900">
                            The requested Portfolio Reassessment is not available for this Investment Program. No unrelated reassessment has been selected.
                        </p>
                    )}
                    {!draft && canPrepareReassessment && (
                        <div className="rounded-xl border bg-white p-5">
                            <p className="text-slate-700">
                                Create a quantitative checkpoint without changing the Management benefits assessment.
                            </p>
                            {existingDraft ? (
                                <button
                                    onClick={() => {
                                        continuationFocusPending.current = true
                                        setContinuingReference(existingDraft.reassessment_reference)
                                        setDraft(existingDraft)
                                    }}
                                    className="mt-4 rounded-lg bg-blue-700 px-4 py-2 font-semibold text-white"
                                >
                                    Open Existing Draft #{existingDraft.sequence}
                                </button>
                            ) : (
                                <div className="mt-4 flex flex-wrap items-end gap-3">
                                    <label className="font-medium">
                                        Reassessment Type
                                        <span className="block text-sm font-normal text-slate-500">
                                            INTERIM is ongoing evidence. FINAL closes the post-investment evidence
                                            cycle.
                                        </span>
                                        <select
                                            aria-label="Reassessment Type"
                                            className={input}
                                            value={newType}
                                            onChange={(event) => setNewType(event.target.value)}
                                        >
                                            <option value="INTERIM">INTERIM</option>
                                            {!finalCompleted && <option value="FINAL">FINAL</option>}
                                        </select>
                                    </label>
                                    <label>Historical authorization<select className={input} aria-label="Historical authorization" value={baselineAV} disabled={authorityHistory.state !== 'ready'} onChange={e => { setBaselineAV(e.target.value); setLegacyCandidate({ state: 'idle' }); setLegacySourceId('') }}><option value="">Select historical authorization</option>{authorityHistory.rows.map(row => <option key={row.authorization_reference} value={row.authorization_reference}>V{row.version_number} · {authorityDate(row.effective_from)} · {authorityLabel(row)}{row.is_current === true ? ' · Current' : ''}</option>)}</select></label>
                                    {authorityHistory.state === 'restricted' && <p>Historical authority details are restricted.</p>}
                                    {authorityHistory.state === 'error' && <button type="button" onClick={() => setHistoryRefresh(n => n + 1)}>Retry authorization history</button>}
                                    {authorityHistory.state === 'ready' && authorityHistory.rows.length === 0 && <p>No authorization history is available.</p>}
                                    {authorityHistory.rows.find(row => row.authorization_reference === baselineAV)?.provenance?.status === 'LEGACY_INCOMPLETE' && <p>Historical authority preserved. Analytical evidence was not captured under this contract.</p>}
                                    <label className="block"><input type="checkbox" checked={legacyMode} disabled={!baselineAV || !canLegacy} onChange={e => { setLegacyMode(e.target.checked); setLegacyCandidate({ state: 'idle' }); setLegacySourceId('') }} /> Use governed legacy historical evidence</label>
                                    {!canLegacy && <p>Governed legacy evidence requires access to all evidence domains.</p>}
                                    {!legacyMode ? <>
                                    <label>Historical analytical position<select className={input} value={baselineRole} onChange={e => setBaselineRole(e.target.value)}><option value="">No analytical role selected</option>{['OPTIMIZATION_GROSS', 'OPTIMIZATION_SELECTED', 'PORTFOLIO_GROSS', 'PORTFOLIO_ADJUSTED'].map(role => <option key={role}>{role}</option>)}</select></label>
                                    <label>Evidence relationship<select className={input} value={baselineRelationship} onChange={e => setBaselineRelationship(e.target.value)}><option value="">Select relationship</option>{['PRESENTED', 'CONSIDERED', 'ADOPTED', 'LATER_VALIDATED'].map(role => <option key={role}>{role}</option>)}</select></label>
                                    </> : <section aria-label="Governed legacy candidate" className="space-y-3">
                                        {legacyCandidate.state === 'loading' && <p>Resolving governed legacy evidence…</p>}
                                        {legacyCandidate.state === 'restricted' && <p>Evidence details are restricted by your access.</p>}
                                        {legacyCandidate.state === 'error' && <p role="alert">{legacyCandidate.message}</p>}
                                        {canLegacy && legacyCandidate.state === 'ready' && legacyCandidate.reference === baselineAV && <>
                                            <p>Historical authority: V{legacyCandidate.value.authorization?.version} · {authorityLabel({ authorized_budget: legacyCandidate.value.authorization?.authorized_budget })}</p>
                                            <p>Evidence relationship: {legacyCandidate.value.relationship || 'Unavailable'} · Analytical position: {legacyCandidate.value.analytical_position || 'Unavailable'}</p>
                                            <p>Evidence quality: {legacyCandidate.value.completeness || 'Unavailable'} · Population comparison: {legacyCandidate.value.population_coverage?.population_comparison_status || 'Unavailable'}</p>
                                            <ul>{(legacyCandidate.value.limitations || []).map((text, i) => <li key={i}>{text}</li>)}</ul>
                                            <p>Select eligible approved current governed evidence before creation. The server revalidates eligibility when creating the reassessment.</p>
                                            <label>Current governed evidence<select className={input} value={legacySourceId} onChange={e => setLegacySourceId(e.target.value)}><option value="">Select approved analytical evidence</option>{legacySources.map(row => <option key={row.id} value={row.id}>{row.snapshot_name || 'Approved evidence'} · {row.approved_at || 'Approval date unavailable'}</option>)}</select></label>
                                            {legacySources.length === 0 && <><p>No eligible current governed evidence available. An approved Program-scoped snapshot with exact provenance is required; running analysis alone is insufficient.</p><GovernedNavigation to={`/strategic-analysis?program=${encodeURIComponent(programReference)}&returnTo=${encodeURIComponent(`/benefits-governance/${programReference}`)}`}>Create Current Analytical Evidence</GovernedNavigation></>}
                                        </>}
                                        {canLegacy && <button type="button" disabled={!baselineAV || legacyCandidate.state === 'loading'} onClick={() => {
                                            setLegacyCandidate({ state: 'loading' }); setLegacyRefresh(n => n + 1)
                                        }}>{legacyCandidate.state === 'loading' ? 'Refreshing legacy evidence…' : 'Refresh legacy evidence'}</button>}
                                    </section>}
                                </div>
                            )}
                        </div>
                    )}
                    {canPrepareReassessment && (
                        <ReassessmentCreation showCreate={!draft && !existingDraft} program={programReference} onOpen={setDraft} onDenied={legacyMode ? clearLegacy : undefined}
                            disabled={!canPrepareReassessment || !baselineAV || authorityHistory.state !== 'ready' || authorityHistory.rows.filter(row => row.authorization_reference === baselineAV).length !== 1 || (legacyMode ? (!canLegacy || legacyCandidate.state !== 'ready' || legacyCandidate.reference !== baselineAV || !legacySources.some(row => String(row.id) === legacySourceId)) : (!baselineRole || !baselineRelationship))}
                            payload={{ reassessment_type: newType,
                                ...(legacyMode ? { legacy_authorization_reference: baselineAV, legacy_current_source_id: Number(legacySourceId) } : { baseline_authorization_reference: baselineAV, baseline_position_role: baselineRole, baseline_evidence_relationship: baselineRelationship }),
                                assessment_date: new Date().toISOString().slice(0, 10), management_commentary: '' }} />
                    )}
                    {draft && !restrictedDraft && (!legacyDraft || canLegacy) && (
                        <div className="space-y-4 rounded-xl border border-blue-200 bg-blue-50 p-5">
                            <span className={badge}>Draft</span>
                            <section aria-labelledby="approved-snapshot-heading" className="space-y-2">
                                <h3 id="approved-snapshot-heading" className="font-semibold">
                                    Current Approved Analytical Snapshot
                                </h3>
                                <p className="text-sm text-slate-600">
                                    Select the approved Program-scoped analytical evidence to use as the current risk
                                    position for this reassessment. This action does not rerun FAIR or Portfolio
                                    Analysis.
                                </p>
                                        {!legacyDraft && <label>Current analytical position<select className={input} value={selectedRole} onChange={e => setSelectedRole(e.target.value)}><option value="">Use validated published role</option><option value="PORTFOLIO_GROSS">PORTFOLIO_GROSS</option><option value="PORTFOLIO_ADJUSTED">PORTFOLIO_ADJUSTED</option></select></label>}
                                {snapshotEvidenceLoading ? (
                                    <p role="status" className="rounded-lg border bg-white p-4 text-sm text-slate-600">
                                        Loading approved analytical snapshots…
                                    </p>
                                ) : snapshotsError ? (
                                    <p
                                        role="alert"
                                        className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800"
                                    >
                                        Approved analytical snapshots could not be loaded.
                                    </p>
                                ) : approvedSnapshots.length === 0 && historyError ? (
                                    <p
                                        role="alert"
                                        className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800"
                                    >
                                        Portfolio Snapshot history could not be loaded.
                                    </p>
                                ) : approvedSnapshots.length === 0 && pendingDrafts.length > 0 ? (
                                    <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
                                        <p className="font-semibold text-amber-950">
                                            Current Analytical Evidence — Approval Pending
                                        </p>
                                        <p className="mt-2 font-medium">{snapshotTitle(pendingDrafts[0])}</p>
                                        <p className="mt-1 text-sm">
                                            {new Date(
                                                pendingDrafts[0].created_at || pendingDrafts[0].snapshot_date
                                            ).toLocaleDateString()}{' '}
                                            · EAL{' '}
                                            {snapshotMetric(
                                                pendingDrafts[0].expected_annual_loss,
                                                snapshotCurrency(pendingDrafts[0])
                                            )}{' '}
                                            · P95{' '}
                                            {snapshotMetric(pendingDrafts[0].p95, snapshotCurrency(pendingDrafts[0]))}
                                        </p>
                                        <p className="mt-1 text-sm">
                                            Status DRAFT · Program{' '}
                                            {currentEvidenceContext(pendingDrafts[0]).investment_program_reference} ·
                                            Purpose {currentEvidenceContext(pendingDrafts[0]).purpose}
                                            {currentEvidenceContext(pendingDrafts[0]).measured_at
                                                ? ` · Measured at ${
                                                      currentEvidenceContext(pendingDrafts[0]).measured_at
                                                  }`
                                                : ''}
                                        </p>
                                        {pendingDrafts.length > 1 && (
                                            <p className="mt-2 text-sm font-medium">
                                                {pendingDrafts.length - 1} additional pending draft
                                                {pendingDrafts.length === 2 ? '' : 's'} remain in snapshot history.
                                            </p>
                                        )}
                                        <button
                                            onClick={() => {
                                                const next = new URLSearchParams(searchParams)
                                                next.set('evidence_snapshot', String(pendingDrafts[0].id))
                                                setSearchParams(next)
                                            }}
                                            className="mt-3 rounded-lg bg-blue-700 px-4 py-2 font-semibold text-white"
                                        >
                                            Review Current Analytical Evidence
                                        </button>
                                    </div>
                                ) : approvedSnapshots.length === 0 ? (
                                    <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
                                        <p className="font-semibold text-amber-900">
                                            No eligible current analytical evidence available.
                                        </p>
                                        <p className="mt-1 text-sm text-amber-800">
                                            An approved Program-scoped Portfolio Snapshot with complete provenance is
                                            required before the current risk position can be captured.
                                        </p>
                                        <GovernedNavigation
                                            className="mt-3 inline-flex rounded-lg bg-blue-700 px-4 py-2 font-semibold text-white"
                                            to={`/strategic-analysis?program=${encodeURIComponent(
                                                programReference
                                            )}&returnTo=${encodeURIComponent(
                                                `/benefits-governance/${programReference}`
                                            )}`}
                                        >
                                            Go to Workspace to Create Evidence
                                        </GovernedNavigation>
                                    </div>
                                ) : approvedSnapshots.length === 1 ? (
                                    <ApprovedSnapshotEvidence
                                        snapshot={approvedSnapshots[0]}
                                        currentPosition={draft.current_position}
                                    />
                                ) : (
                                    <>
                                        <label className="block font-medium">
                                            Approved analytical evidence
                                            <select
                                                aria-label="Approved analytical evidence selection"
                                                className={input}
                                                value={sourceId}
                                                onChange={(event) => setSourceId(event.target.value)}
                                            >
                                                <option value="">Select approved analytical evidence</option>
                                                {approvedSnapshots.map((snapshot) => (
                                                    <option key={snapshot.id} value={snapshot.id}>
                                                        {snapshotSummary(snapshot)}
                                                    </option>
                                                ))}
                                            </select>
                                        </label>
                                        {sourceId && (
                                            <ApprovedSnapshotEvidence
                                                snapshot={approvedSnapshots.find(
                                                    (snapshot) => String(snapshot.id) === String(sourceId)
                                                )}
                                                currentPosition={draft.current_position}
                                            />
                                        )}
                                    </>
                                )}
                                {draft.current_position &&
                                    Object.keys(draft.current_position).length > 0 &&
                                    !approvedSnapshots.some(
                                        (snapshot) => String(snapshot.id) === String(draft.current_source_id)
                                    ) && <ApprovedSnapshotEvidence currentPosition={draft.current_position} />}
                            </section>
                            <label className="block font-medium">
                                Assessment Date
                                <input
                                    aria-label="Reassessment Date"
                                    type="date"
                                    className={input}
                                    value={draft.assessment_date || ''}
                                    onChange={(e) => setDraft({ ...draft, assessment_date: e.target.value })}
                                />
                            </label>
                            <label className="block font-medium">
                                Value Realization Evidence — Optional
                                <select
                                    aria-label="Value Realization Evidence"
                                    className={input}
                                    value={draft.value_realization_evidence?.assessment_reference || ''}
                                    onChange={(event) => setDraft({
                                        ...draft,
                                        value_realization_evidence: valueEvidenceCandidates.find(
                                            (candidate) => candidate.assessment_reference === event.target.value
                                        ) || null
                                    })}
                                >
                                    <option value="">No Value Realization evidence selected</option>
                                    {valueEvidenceCandidates.map((candidate) => (
                                        <option key={candidate.assessment_reference} value={candidate.assessment_reference}>
                                            {candidate.reporting_period.start} – {candidate.reporting_period.end} · {candidate.assessment_as_of || 'Assessment date unavailable'} · Authorization v{candidate.authorization?.version}
                                        </option>
                                    ))}
                                </select>
                            </label>
                            <ValueRealizationEvidence evidence={draft.value_realization_evidence} />
                            <label className="block font-medium">
                                Reassessment Trigger
                                <select
                                    aria-label="Reassessment Trigger"
                                    className={input}
                                    value={draft.reassessment_trigger}
                                    onChange={(e) => setDraft({ ...draft, reassessment_trigger: e.target.value })}
                                >
                                    {[
                                        ['SCHEDULED_REVIEW', 'Scheduled review'],
                                        ['MAJOR_INVESTMENT_COMPLETED', 'Major investment completed'],
                                        ['MATERIAL_RISK_CHANGE', 'Material risk change'],
                                        ['BOARD_REQUEST', 'Board request'],
                                        ['REGULATORY_REQUIREMENT', 'Regulatory requirement'],
                                        ['MANAGEMENT_REQUEST', 'Management request'],
                                        ['OTHER', 'Other']
                                    ].map(([key, label]) => (
                                        <option key={key} value={key}>
                                            {label}
                                        </option>
                                    ))}
                                </select>
                            </label>
                            {isCapturedDraft && !legacyDraft && <ReassessmentEvidence reassessment={draft} />}
                            {isCapturedDraft && <h3 className="text-lg font-semibold">Management Assessment</h3>}
                            {isCapturedDraft && (
                                <div className="grid gap-4 md:grid-cols-2">
                                    <label className="font-medium">
                                        Management materiality judgment
                                    {legacyDraft && <span className="block text-sm font-normal">Unable to determine materiality from the available comparable evidence.</span>}
                                        <select
                                            aria-label="Management materiality judgment"
                                            className={input}
                                            value={materialitySelected ? draft.material_change_assessment : ''}
                                            onChange={(e) =>
                                                { setMaterialitySelected(true); setDraft({ ...draft, material_change_assessment: e.target.value }) }
                                            }
                                        >
                                            <option value="">Select a materiality judgment</option>
                                            {[
                                                ['UNDETERMINED', 'Undetermined'],
                                                ['MATERIAL', 'Material'],
                                                ['NOT_MATERIAL', 'Not material']
                                            ].filter(([key]) => !legacyDraft || !['MATERIAL', 'NOT_MATERIAL', 'CONTINUE_MONITORING', 'NO_FURTHER_ACTION'].includes(key)).map(([key, label]) => (
                                                <option key={key} value={key}>
                                                    {label}
                                                </option>
                                            ))}
                                        </select>
                                    </label>
                                    <label className="font-medium">
                                        Management Recommendation
                                        <select
                                            aria-label="Management Recommendation"
                                            className={input}
                                            value={draft.management_recommendation || ''}
                                            onChange={(e) =>
                                                setDraft({ ...draft, management_recommendation: e.target.value })
                                            }
                                            disabled={!canRecommendReassessment}
                                        >
                                            <option value="">Select a recommendation</option>
                                            {[
                                                ['CONTINUE_MONITORING', 'Continue monitoring'],
                                                ['REASSESS_LATER', 'Reassess later'],
                                                ['FURTHER_ANALYSIS_REQUIRED', 'Further analysis required'],
                                                ['ESCALATE_TO_MANAGEMENT', 'Escalate to Management'],
                                                ['ESCALATE_TO_BOARD', 'Escalate to Board'],
                                                ['NO_FURTHER_ACTION', 'No further action']
                                            ].filter(([key]) => !legacyDraft || !['MATERIAL', 'NOT_MATERIAL', 'CONTINUE_MONITORING', 'NO_FURTHER_ACTION'].includes(key)).map(([key, label]) => (
                                                <option key={key} value={key}>
                                                    {label}
                                                </option>
                                            ))}
                                        </select>
                                    </label>
                                    {draft.reassessment_type !== 'FINAL' && (
                                        <label className="font-medium md:col-span-2">
                                            Continued Investment Conclusion
                                            <span className="block text-sm font-normal text-slate-500">
                                                Management's conclusion on whether current evidence supports continued
                                                execution under the existing authorization.
                                            </span>
                                            <select
                                                aria-label="Continued Investment Conclusion"
                                                className={input}
                                                value={draft.continued_investment_conclusion || ''}
                                                onChange={(e) =>
                                                    setDraft({
                                                        ...draft,
                                                        continued_investment_conclusion: e.target.value
                                                    })
                                                }
                                            >
                                                <option value="">Select a conclusion</option>
                                                {!legacyDraft && <option value="SUPPORTED">Supported under existing authorization</option>}
                                                <option value="INSUFFICIENT_EVIDENCE">Insufficient evidence</option>
                                                {!legacyDraft && <option value="EXISTING_AUTHORIZATION_INSUFFICIENT">
                                                    Existing authorization insufficient
                                                </option>}
                                                {!legacyDraft && <option value="NO_LONGER_SUPPORTED">
                                                    Continued investment no longer supported
                                                </option>}
                                            </select>
                                        </label>
                                    )}
                                </div>
                            )}
                            <label className="block font-medium">
                                Management Commentary
                                <span className="block text-sm font-normal text-slate-500">
                                    Explain the observed change, limitations, materiality judgment, and recommendation.
                                </span>
                                <textarea
                                    aria-label="Reassessment Management Commentary"
                                    className={input}
                                    value={draft.management_commentary || ''}
                                    onChange={(e) => setDraft({ ...draft, management_commentary: e.target.value })}
                                />
                            </label>
                            <p className="text-sm text-slate-600">
                                Copies approved evidence into this draft. The approved source evidence is not modified.
                            </p>
                            <div className="flex gap-3">
                                <button
                                    disabled={!canPrepareReassessment || !sourceId || snapshotEvidenceLoading || snapshotsError}
                                    onClick={capture}
                                    className="rounded-lg border border-blue-700 px-4 py-2 font-semibold text-blue-800 disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                    {isCapturedDraft ? 'Recapture Current Position' : 'Capture Current Risk Position'}
                                </button>
                                {isCapturedDraft && assessment.portfolio_reassessment_permissions?.complete && access.hasPermission(PERMISSIONS.benefits.reassessmentComplete) && (
                                    <button
                                        ref={completeButtonRef}
                                        onClick={() => setCompletionDialogOpen(true)}
                                        disabled={(legacyDraft && (draft.material_change_assessment !== 'UNDETERMINED' || !['REASSESS_LATER', 'FURTHER_ANALYSIS_REQUIRED', 'ESCALATE_TO_MANAGEMENT', 'ESCALATE_TO_BOARD'].includes(draft.management_recommendation) || (draft.reassessment_type !== 'FINAL' && draft.continued_investment_conclusion !== 'INSUFFICIENT_EVIDENCE'))) || !materialitySelected || !draft.management_commentary?.trim() || !draft.management_recommendation || !draft.completion_context?.supported || !canRecommendReassessment || (draft.reassessment_type !== 'FINAL' && !draft.continued_investment_conclusion)}
                                        title={
                                            draft.reassessment_type !== 'FINAL' &&
                                            !draft.continued_investment_conclusion
                                                ? 'Select a continued investment conclusion before completion.'
                                                : undefined
                                        }
                                        className="rounded-lg bg-blue-700 px-4 py-2 font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50"
                                    >
                                        Complete and Freeze
                                    </button>
                                )}
                            </div>
                        </div>
                    )}
                    {completionDialogOpen && draft && !restrictedDraft && (!legacyDraft || canLegacy) && (
                        <PortfolioReassessmentCompletionDialog
                            completing={completing}
                            context={draft.completion_context}
                            onCancel={cancelCompletion}
                            onConfirm={complete}
                        />
                    )}
                    {reassessment?.available === false && (
                        <p className="rounded-xl border border-amber-200 bg-amber-50 p-5 text-amber-900">
                            {reassessment.reason}
                        </p>
                    )}
                    {history.length > 0 && (
                        <div className="rounded-xl border bg-white p-5">
                            <h3 className="font-semibold">Reassessment History</h3>
                            <div className="mt-3 space-y-2">
                                {history.map((item) => (
                                    <details
                                        key={item.reassessment_reference}
                                        open={String(item.reassessment_reference) === requestedReassessmentReference}
                                        className={`rounded-lg border p-3 ${String(item.reassessment_reference) === requestedReassessmentReference ? 'border-blue-500 bg-blue-50' : ''}`}
                                        data-context-selected={String(item.reassessment_reference) === requestedReassessmentReference ? 'true' : undefined}
                                    >
                                        <summary className="cursor-pointer font-medium">
                                            #{item.sequence} {item.reassessment_type || 'INTERIM'} — {item.status}
                                        </summary>
                                        {reassessmentRestricted(item) ? <p className="mt-3 text-sm">Evidence details are restricted by your access.</p> : <>
                                        <dl className="mt-3 grid gap-2 text-sm sm:grid-cols-3">
                                            <div>
                                                <dt className="text-slate-500">Assessment date</dt>
                                                <dd>{item.assessment_date || 'Unavailable'}</dd>
                                            </div>
                                            <div>
                                                <dt className="text-slate-500">Execution checkpoint</dt>
                                                <dd>
                                                    {item.execution_progress_at_checkpoint == null
                                                        ? 'Unavailable'
                                                        : `${item.execution_progress_at_checkpoint}%`}
                                                </dd>
                                            </div>
                                            <div>
                                                <dt className="text-slate-500">Comparability</dt>
                                                <dd>{comparabilityLabel(item.comparability?.status)}</dd>
                                            </div>
                                            <div>
                                                <dt className="text-slate-500">Materiality</dt>
                                                <dd>{item.material_change_assessment_display || 'Unavailable'}</dd>
                                            </div>
                                            <div>
                                                <dt className="text-slate-500">Management recommendation</dt>
                                                <dd>{item.management_recommendation_display || 'Unavailable'}</dd>
                                            </div>
                                            {item.reassessment_type === 'INTERIM' && (
                                                <div>
                                                    <dt className="text-slate-500">Continued Investment Conclusion</dt>
                                                    <dd>{item.continued_investment_conclusion_display || 'Not recorded'}</dd>
                                                </div>
                                            )}
                                            <div>
                                                <dt className="text-slate-500">Evidence captured</dt>
                                                <dd>{item.execution_checkpoint_captured_at || 'Unavailable'}</dd>
                                            </div>
                                        </dl>
                                        <ValueRealizationEvidence evidence={item.value_realization_evidence} />
                                        </>}
                                    </details>
                                ))}
                            </div>
                        </div>
                    )}
                    {reassessment?.status === 'COMPLETED' && reassessment.baseline_position?.detail_access !== 'ACCESS_RESTRICTED' && (!isLegacyObservation(reassessment) || canLegacy) && (
                        <div className="space-y-5">
                            <div className="rounded-xl border bg-white p-5">
                                <h3 className="font-semibold">Reassessment Summary</h3>
                                <div className="mt-3 flex flex-wrap gap-2">
                                    <span className={badge}>Status: Completed</span>
                                    <span className={badge}>Trigger: {reassessment.reassessment_trigger_display}</span>
                                    <span className={badge}>
                                        {comparabilityLabel(reassessment.comparability?.status)}
                                    </span>
                                </div>
                                {reassessment.comparability?.reasons?.length > 0 && (
                                    <ul className="mt-3 list-disc pl-5 text-sm text-slate-600">
                                        {reassessment.comparability.reasons.map((reason) => (
                                            <li key={`${reason.check}-${reason.code}`}>
                                                {reason.code.replaceAll('_', ' ').toLowerCase()}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                                <p className="mt-3 font-mono text-sm">{reassessment.reassessment_reference}</p>
                                <p className="mt-2 text-sm text-slate-600">
                                    Assessed {reassessment.assessment_date} by {reassessment.assessed_by}
                                </p>
                            </div>
                            {!isLegacyObservation(reassessment) && <ReassessmentEvidence reassessment={reassessment} />}
                            <ValueRealizationEvidence evidence={reassessment.value_realization_evidence} />
                            <div className="rounded-xl border bg-white p-5">
                                <h3 className="font-semibold">Scenario Comparison</h3>
                                {reassessment.observed_change?.scenarios?.length ? (
                                    <ul className="mt-3 space-y-2">
                                        {reassessment.observed_change.scenarios.map((row, index) => {
                                            const modern = reassessment.observed_change.observed_change_schema_version === 2
                                            const identity = modern ? row.scenario_id : row.scenario_reference
                                            const metric = reassessment.comparability?.scenario_metrics?.[identity]
                                            const permitted = modern && metric?.difference_allowed === true &&
                                                ['COMPARABLE', 'PARTIALLY_COMPARABLE'].includes(metric?.status) && row.change != null
                                            return <li key={identity || `unavailable-${index}`}>
                                                {identity != null ? `Scenario #${identity}` : 'Scenario identity unavailable'}
                                                {row.scenario_name ? ` · ${row.scenario_name}` : ''}
                                                {modern && <span> · Scenario EAL: {permitted ? changeValue(row) : 'Difference unavailable'}</span>}
                                            </li>
                                        })}
                                    </ul>
                                ) : (
                                    <p className="mt-2 text-slate-600">
                                        Unavailable — no exact scenario references matched.
                                    </p>
                                )}
                            </div>
                            <div className="rounded-xl border bg-white p-5">
                                <h3 className="font-semibold">Management Governance Conclusion</h3>
                                <div className="mt-3 flex flex-wrap gap-2">
                                    <span className={badge}>
                                        Material change: {reassessment.material_change_assessment_display}
                                    </span>
                                    <span className={badge}>
                                        Recommendation: {reassessment.management_recommendation_display}
                                    </span>
                                </div>
                                {reassessment.reassessment_type === 'INTERIM' && (
                                    <dl className="mt-4">
                                        <dt className="text-sm text-slate-500">Continued Investment Conclusion</dt>
                                        <dd>
                                            {reassessment.continued_investment_conclusion_display ?? 'Not recorded'}
                                        </dd>
                                    </dl>
                                )}
                                <p className="mt-4 whitespace-pre-wrap">
                                    {reassessment.management_commentary || 'Not captured'}
                                </p>
                                <p className="mt-3 text-sm">Reassessment completion leaves current authority unchanged.</p>
                                <p className="mt-2 text-sm">{reassessment.completion?.recorded
                                    ? `Completed by ${reassessment.completion.completed_by?.username} at ${reassessment.completion.completed_at}`
                                    : 'Completion provenance not recorded'}</p>

                            </div>
                            <div className="rounded-xl border bg-white p-5">
                                <h3 className="font-semibold">Source and Data Quality</h3>
                                <p className="mt-2">
                                    Approved Portfolio Snapshot ·{' '}
                                    {reassessment.source_reference_available
                                        ? `Reference ${reassessment.current_source_id}`
                                        : 'Reference unavailable'}
                                </p>
                                <p className="text-sm text-slate-600">
                                    Generated {reassessment.current_source_generated_at || 'Not captured'} · schema
                                    version {reassessment.portfolio_reassessment_schema_version}
                                </p>
                                <p className="mt-2 text-sm text-slate-600">
                                    Unavailable or currency-incompatible metrics are not compared. Scenario matching
                                    uses exact references only.
                                </p>
                            </div>
                            <details className="rounded-xl border bg-white p-5">
                                <summary className="cursor-pointer font-semibold">Technical Lineage</summary>
                                <p className="mt-3 text-sm">
                                    Proposal {assessment.proposal_reference} · Program {assessment.program_reference} ·
                                    Source {reassessment.current_source_id || 'Reference unavailable'}
                                </p>
                            </details>
                        </div>
                    )}
                </ExecutiveSection>
                <ExecutiveSection
                    title="Governance Timeline"
                    subtitle="Where does Benefits Governance sit in the executive governance lifecycle?"
                >
                    <ol className="mx-auto flex max-w-3xl flex-col gap-2">
                        {timeline.map((step, index) => (
                            <React.Fragment key={step}>
                                <li
                                    className={`rounded-xl border p-4 text-center font-semibold ${
                                        step === 'Benefits Governance'
                                            ? 'border-blue-300 bg-blue-50 text-blue-800'
                                            : 'bg-white'
                                    }`}
                                >
                                    {step}
                                </li>
                                {index < timeline.length - 1 && (
                                    <li aria-hidden="true" className="text-center text-xl text-slate-400">
                                        ↓
                                    </li>
                                )}
                            </React.Fragment>
                        ))}
                    </ol>
                </ExecutiveSection>
                <details className="rounded-2xl border bg-white p-5">
                    <summary className="cursor-pointer font-bold text-slate-800">Technical Lineage</summary>
                    <p className="mt-1 text-sm text-slate-500">
                        Immutable references connecting authorization to this assessment.
                    </p>
                    <dl className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
                        {[
                            ['Proposal Reference', assessment.proposal_reference],
                            ['Snapshot Version', assessment.snapshot_version],
                            ['Board Decision Reference', assessment.board_decision_reference],
                            ['Investment Program Reference', assessment.program_reference],
                            ['Assessment Version', assessment.assessment_version]
                        ].map(([label, value]) => (
                            <div className="min-w-0" key={label}>
                                <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                                    {label}
                                </dt>
                                <dd className="mt-1 break-all font-mono text-xs text-slate-700">{value}</dd>
                            </div>
                        ))}
                    </dl>
                </details>

            </div>
        </main>
    )
}
